import type { DiGraph } from "./digraph.js";

/**
 * Power iteration PageRank. Dangling nodes receive teleport mass evenly (standard fix).
 * Returns a map: nodeId -> score (sum to 1 over all nodes in the graph).
 */
export function pageRank(
  g: DiGraph,
  options: { damping?: number; epsilon?: number; maxIter?: number } = {}
): Map<string, number> {
  const d = options.damping ?? 0.85;
  const epsilon = options.epsilon ?? 1e-10;
  const maxIter = options.maxIter ?? 200;
  const nodes = g.nodes;
  const n = nodes.length;
  if (n === 0) return new Map();

  const nodeIndex = new Map(nodes.map((v, i) => [v, i] as const));
  const invN = 1 / n;
  const r = new Float64Array(n);
  r.fill(invN);
  const rNext = new Float64Array(n);
  const dangling = new Int32Array(n);

  for (const v of nodes) {
    const i = nodeIndex.get(v)!;
    dangling[i] = g.isDangling(v) ? 1 : 0;
  }

  for (let it = 0; it < maxIter; it++) {
    rNext.fill((1 - d) * invN);
    let danglingSum = 0;
    for (let i = 0; i < n; i++) {
      if (dangling[i]) danglingSum += r[i]!;
    }
    const redist = d * danglingSum * invN;
    for (let i = 0; i < n; i++) {
      rNext[i]! += redist;
    }

    for (const u of nodes) {
      const ui = nodeIndex.get(u)!;
      const wOut = g.outWeight(u);
      if (wOut === 0) continue;
      for (const { v, w: wt } of g.outNeighbors(u)) {
        const vi = nodeIndex.get(v)!;
        rNext[vi]! += (d * (r[ui]! * wt)) / wOut;
      }
    }

    let diff = 0;
    for (let i = 0; i < n; i++) {
      diff += Math.abs(rNext[i]! - r[i]!);
    }
    for (let i = 0; i < n; i++) {
      r[i] = rNext[i]!;
    }
    if (diff < epsilon) break;
  }

  const out = new Map<string, number>();
  for (const v of nodes) {
    out.set(v, r[nodeIndex.get(v)!]!);
  }
  return out;
}
